import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { addDoc, updateDoc } from "firebase/firestore";
import {
  getLifestyleDoc,
  lifestyleCollectionRef,
  uploadImgLifestyle,
} from "../utils/firestoreHelpers";
import { useState } from "react";
import { formatDate } from "../utils/helpers";

const initialData = {
  title: "",
  link: "",
  type: "",
  imageUrl: "",
  dateFrom: "",
  dateTo: "",
  description: "",
  venue: "",
  tnc: "",
  showIsInterested: "yes",
};

const initialErrorData = {
  title: "",
  link: "",
  type: "",
  imageUrl: "",
  dateFrom: "",
  dateTo: "",
  description: "",
  venue: "",
  tnc: "",
  img: "",
  showIsInterested: "",
};

const EditLifestyle = ({ onClose, prevData, dialogType, setDialogType }) => {
  const [data, setData] = useState(
    prevData
      ? {
          ...prevData,
          dateFrom: formatDate(prevData.date_from),
          dateTo: formatDate(prevData.date_to),
          showIsInterested: prevData.showIsInterested ? "yes" : "no",
        }
      : { ...initialData }
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ ...initialErrorData });
  const [img, setImg] = useState(null);

  // const validate = () => {
  //   const { title, type, dateFrom, link, showIsInterested } = data;
  //   let isValid = true;
  //   let tempError = { ...initialErrorData };
  //   if (!title) {
  //     isValid = false;
  //     tempError["title"] = "Title is required";
  //   }
  //   if (!type) {
  //     isValid = false;
  //     tempError["type"] = "Type is required";
  //   }
  //   if (!dateFrom) {
  //     isValid = false;
  //     tempError["dateFrom"] = "Date is required";
  //   }
  //   if (!link) {
  //     isValid = false;
  //     tempError["date"] = "Date is required";
  //   }
  //   if (!["yes", "no"].includes(showIsInterested)) {
  //     isValid = false;
  //     tempError["showIsInterested"] = "Please select a valid value";
  //   }
  //   if (!img && !prevData?.image_url) {
  //     isValid = false;
  //     tempError["img"] = "Image is required";
  //   }
  //   if (!isValid) {
  //     setError(tempError);
  //   }
  //   return isValid;
  // };

  const validate = () => {
    const {
      title,
      type,
      description,
      venue,
      tnc,
      dateFrom,
      dateTo,
      link,
      showIsInterested,
    } = data;

    let isValid = true;
    let tempError = { ...initialErrorData };

    // Title (REQUIRED + 3–200)
    if (!title?.trim()) {
      isValid = false;
      tempError.title = "Title is required";
    } else if (title.length < 3 || title.length > 200) {
      isValid = false;
      tempError.title = "Title must be between 3 and 200 characters";
    }

    // Type (REQUIRED + 3–200)
    if (!type?.trim()) {
      isValid = false;
      tempError.type = "Type is required";
    } else if (type.length < 3 || type.length > 200) {
      isValid = false;
      tempError.type = "Type must be between 3 and 200 characters";
    }

    // Description (OPTIONAL)
    if (description?.trim() && (description.length < 3 || description.length > 500)) {
      isValid = false;
      tempError.description = "Description must be between 3 and 500 characters";
    }

    // Venue (OPTIONAL)
    if (venue?.trim() && (venue.length < 3 || venue.length > 500)) {
      isValid = false;
      tempError.venue = "Venue must be between 3 and 500 characters";
    }

    // Terms (OPTIONAL)
    if (tnc?.trim() && (tnc.length < 3 || tnc.length > 500)) {
      isValid = false;
      tempError.tnc = "Terms must be between 3 and 500 characters";
    }

    // Date From (REQUIRED + no past)
    if (!dateFrom) {
      isValid = false;
      tempError.dateFrom = "Date is required";
    } else {
      const today = new Date().setHours(0, 0, 0, 0);
      const selectedDate = new Date(dateFrom).setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        isValid = false;
        tempError.dateFrom = "Past date is not allowed";
      }
    }

    // Date To (OPTIONAL but must be >= dateFrom)
    if (dateTo && dateFrom) { 
      const from = new Date(dateFrom);
      const to = new Date(dateTo);

      if (to < from) {
        isValid = false;
        tempError.dateTo = "End date cannot be before start date";
      }
    }

    //  Link (REQUIRED + valid URL)
    if (!link?.trim()) {
      isValid = false;
      tempError.link = "Link is required";
    } else if (!/^https?:\/\/.+\..+/.test(link)) {
      isValid = false;
      tempError.link = "Enter a valid URL";
    }

    //  Show Interested (REQUIRED)
    if (!["yes", "no"].includes(showIsInterested)) {
      isValid = false;
      tempError.showIsInterested = "Please select a valid value";
    }

    //  Image (REQUIRED)
    if (!img && !prevData?.image_url) {
      isValid = false;
      tempError.img = "Image is required";
    } else if (img) {
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(img.type)) {
        isValid = false;
        tempError.img = "Only PNG, JPG, JPEG images are allowed";
      }
    }

    if (!isValid) {
      setError(tempError);
    }

    return isValid;
  };

  const onSubmit = async () => {
    let isValid = validate();
    if (!isValid) return;
    setLoading(true);
    const {
      title,
      link,
      type,
      dateFrom,
      dateTo,
      description,
      venue,
      tnc,
      showIsInterested,
    } = data;
    if (prevData) {
      let imageUrl = prevData.image_url;
      const bussinessDoc = getLifestyleDoc(prevData.id);
      if (img) {
        imageUrl = await uploadImgLifestyle(img);
      }
      const newFields = {
        title,
        link,
        type,
        image_url: imageUrl,
        date_from: new Date(dateFrom),
        date_to: dateTo ? new Date(dateTo) : null,
        description,
        venue,
        tnc,
        showIsInterested: showIsInterested === "yes",
      };
      await updateDoc(bussinessDoc, newFields);
    } else {
      if (!img) return;
      const imageUrl = await uploadImgLifestyle(img);
      const payload = {
        title,
        link,
        type,
        image_url: imageUrl,
        date_from: new Date(dateFrom),
        date_to: dateTo ? new Date(dateTo) : null,
        description,
        venue,
        tnc,
        showIsInterested: showIsInterested === "yes",
      };
      await addDoc(lifestyleCollectionRef, payload);
    }
    setLoading(false);
    onClose();
  };

  const onChange = (e) => {
    let value = e.target.value;
    const name = e.target.name;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const [fileSizeError, setFileSizeError] = useState("");
  const fileSizeLimit = 1 * 1024 * 1024; // 1 MB limit

  const checkFileSize = (event) => {
    const fileInput = event.target;

    if (fileInput.files.length > 0 && fileInput.files[0].size > fileSizeLimit) {
      setFileSizeError("File size exceeds the 1 mb limit.");
      fileInput.value = ""; // Clear the input
    } else {
      setImg(event.target.files[0]);
      setFileSizeError("");
    }
  };

  return (
    <form>
      <Modal show={true} onHide={() => {}}>
        <Modal.Header>
          <Modal.Title>{`${dialogType} Lifestyle`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label htmlFor="title" className="form-label d-block">
              Title *
            </label>
            <input
              value={data.title}
              onChange={onChange}
              className="w-100 form-control"
              name="title"
              required
              minLength={3} maxLength={200}
            />
            <p className="text-danger small">{error.title}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="type" className="form-label d-block">
              Type *
            </label>
            <input
              value={data.type}
              onChange={onChange}
              className="w-100 form-control"
              name="type"
              required
              minLength={3} maxLength={200}
            />
            <p className="text-danger small">{error.type}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="link" className="form-label d-block">
              Link *
            </label>
            <input
              value={data.link}
              onChange={onChange}
              className="w-100 form-control"
              name="link"
              required
            />
            <p className="text-danger small">{error.link}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="image" className="form-label d-block">
              Image *
            </label>
            <input
              type="file"
              className="w-100 form-control"
              name="image"
              // onChange={(e) => setImg(e.target.files[0])}
              onChange={checkFileSize}
              accept="image/png, image/jpeg, image/jpg"
            />
            <p className="text-danger small mt-1">{fileSizeError}</p>
            <p className="text-danger small">{error.img}</p>
          </div>
          {(data?.image_url || img) && (
            <div className="mb-3">
              <img
                src={img ? URL.createObjectURL(img) : data?.image_url}
                height={150}
                width={200}
                alt="preview"
              />
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="showIsInterested" className="form-label d-block">
              Show Is Interested Buttons *
            </label>{" "}
            <div className="d-flex gap-4">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  name="showIsInterested"
                  value={"yes"}
                  id="yes-is-interested"
                  onChange={onChange}
                  checked={data.showIsInterested === "yes"}
                />
                <label class="form-check-label" for="yes-is-interested">
                  Yes
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  value="no"
                  name="showIsInterested"
                  id="no-is-interested"
                  onChange={onChange}
                  checked={data.showIsInterested === "no"}
                />
                <label class="form-check-label" for="no-is-interested">
                  No
                </label>
              </div>
            </div>
            <p className="text-danger small">{error.showIsInterested}</p>
          </div>
          <div className="d-flex gap-2">
            <div className="mb-3 flex-fill">
              <label htmlFor="dateFrom" className="form-label d-block">
                Date From / Event Date *
              </label>
              <input
                type="date"
                className="w-100 form-control"
                name="dateFrom"
                value={data.dateFrom}
                onChange={onChange}
                required
                min={new Date().toISOString().split("T")[0]}
              />
              <p className="text-danger small">{error.dateFrom}</p>
            </div>

            <div className="mb-3 flex-fill">
              <label htmlFor="dateTo" className="form-label d-block">
                Date To
              </label>
              <input
                type="date"
                className="w-100 form-control"
                name="dateTo"
                value={data.dateTo}
                onChange={onChange}
                required
                min={data.dateFrom || new Date().toISOString().split("T")[0]}
              />
              <p className="text-danger small">{error.dateTo}</p>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="description" className="form-label d-block">
              Description
            </label>
            <textarea
              value={data.description}
              className="w-100 form-control"
              name="description"
              onChange={onChange}
              minLength={3} maxLength={500}
            />
             <p className="text-danger small">{error.description}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="venue" className="form-label d-block">
              Venue
            </label>
            <textarea
              value={data.venue}
              className="w-100 form-control"
              name="venue"
              onChange={onChange}
               minLength={3} maxLength={500}
            />
             <p className="text-danger small">{error.venue}</p>
          </div>
          <div className="mb-3">
            <label htmlFor="tnc" className="form-label d-block">
              Terms and condition
            </label>
            <textarea
              value={data.tnc}
              className="w-100 form-control"
              name="tnc"
              onChange={onChange}
              minLength={3} maxLength={500}
            />
             <p className="text-danger small">{error.tnc}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={loading}>
            {loading ? (
              <>
                <span
                  class="spinner-grow spinner-grow-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Loading...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default EditLifestyle;
